import React, { useEffect, useState, memo } from 'react';
import { Settings, Pause, Square, Video, Mic, X, CheckCircle, Play } from 'lucide-react';
type RecordingMode = 'record' | 'journal' | 'upload';
type RecordingScreenProps = {
  onEnd: () => void;
};
export function RecordingScreen({
  onEnd
}: RecordingScreenProps) {
  const [mode, setMode] = useState<RecordingMode>('record');
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [showEndModal, setShowEndModal] = useState(false);
  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isRecording && !isPaused) {
      interval = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording, isPaused]);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const handleRecord = () => {
    setIsRecording(true);
    setIsPaused(false);
  };
  const handlePause = () => {
    setIsPaused(!isPaused);
  };
  const handleStop = () => {
    setShowEndModal(true);
  };
  const handleEndConfirm = () => {
    setShowEndModal(false);
    onEnd();
  };
  return <div className="flex-1 flex flex-col h-screen bg-gray-100" data-id="element-1476">
      {/* Top Bar - Timer (when recording) or Tabs (when not recording) */}
      {isRecording ? <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-1477">
          <div className="flex items-center gap-3" data-id="element-1478">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" data-id="element-1479"></div>
            <span className="font-mono text-2xl font-bold text-gray-900" data-id="element-1480">
              {formatTime(duration)}
            </span>
            {isPaused && <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-full" data-id="element-1481">
                Paused
              </span>}
          </div>
          <button onClick={() => setShowEndModal(true)} className="p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1482">
            <X className="w-6 h-6" data-id="element-1483" />
          </button>
        </div> : <div className="bg-white border-b border-gray-200 px-6 py-3" data-id="element-1484">
          <div className="flex items-center gap-2" data-id="element-1485">
            <button onClick={() => setMode('record')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all ${mode === 'record' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1486">
              <Video className="w-4 h-4" data-id="element-1487" />
              Record
            </button>
            <button onClick={() => setMode('journal')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all ${mode === 'journal' ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1488">
              Journal
            </button>
            <button onClick={() => setMode('upload')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all ${mode === 'upload' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1489">
              Upload
            </button>
          </div>
        </div>}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col" data-id="element-1490">
        {mode === 'record' && <>
            {/* Camera Preview */}
            <div className="flex-1 bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center relative" data-id="element-1491">
              {/* Placeholder for camera feed */}
              <div className="text-gray-400 text-center" data-id="element-1492">
                <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm" data-id="element-1493">
                  <Video className="w-12 h-12" data-id="element-1494" />
                </div>
                <p className="text-lg font-medium" data-id="element-1495">Camera Preview</p>
                <p className="text-sm opacity-75 mt-2" data-id="element-1496">Ready to record</p>
              </div>

              {/* Audio Level Indicator */}
              {isRecording && <div className="absolute bottom-8 left-8" data-id="element-1497">
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-5 w-48" data-id="element-1498">
                    <div className="flex items-center gap-3 mb-3" data-id="element-1499">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center" data-id="element-1500">
                        <Mic className="w-5 h-5 text-green-600" data-id="element-1501" />
                      </div>
                      <span className="text-sm font-semibold text-gray-900" data-id="element-1502">
                        Audio Level
                      </span>
                    </div>
                    <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden" data-id="element-1503">
                      <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-150" style={{
                  width: '70%'
                }} data-id="element-1504"></div>
                    </div>
                  </div>
                </div>}

              {/* Recording Status */}
              {isRecording && <div className="absolute top-8 left-8" data-id="element-1505">
                  <div className="bg-red-500 text-white px-4 py-2 rounded-full flex items-center gap-2 shadow-lg" data-id="element-1506">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" data-id="element-1507"></div>
                    <span className="text-sm font-semibold" data-id="element-1508">Recording</span>
                  </div>
                </div>}
            </div>

            {/* Bottom Controls */}
            <div className="bg-white border-t border-gray-200 px-8 py-6" data-id="element-1509">
              <div className="flex items-center justify-center gap-6 max-w-2xl mx-auto" data-id="element-1510">
                {/* Settings */}
                <button className="p-3 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-1511">
                  <Settings className="w-6 h-6" data-id="element-1512" />
                </button>

                {/* Pause Button (when recording) */}
                {isRecording && <button onClick={handlePause} className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors shadow-lg" data-id="element-1513">
                    {isPaused ? <Play className="w-7 h-7 text-gray-700 ml-1" data-id="element-1514" /> : <Pause className="w-7 h-7 text-gray-700" data-id="element-1515" />}
                  </button>}

                {/* Record/Stop Button */}
                {!isRecording ? <button onClick={handleRecord} className="flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold text-lg rounded-2xl hover:from-red-600 hover:to-red-700 transition-all shadow-2xl" data-id="element-1516">
                    <div className="w-5 h-5 bg-white rounded-full" data-id="element-1517"></div>
                    Start Recording
                  </button> : <button onClick={handleStop} className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center hover:from-red-600 hover:to-red-700 transition-all shadow-2xl" data-id="element-1518">
                    <Square className="w-8 h-8 text-white fill-white" data-id="element-1519" />
                  </button>}

                {/* End Session */}
                <button onClick={() => setShowEndModal(true)} className="p-3 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-1520">
                  <X className="w-6 h-6" data-id="element-1521" />
                </button>
              </div>
            </div>
          </>}

        {mode === 'journal' && <div className="flex-1 flex items-center justify-center p-8" data-id="element-1522">
            <div className="max-w-3xl w-full" data-id="element-1523">
              <div className="bg-white rounded-3xl shadow-xl p-8" data-id="element-1524">
                <div className="flex items-center gap-3 mb-6" data-id="element-1525">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center" data-id="element-1526">
                    <span className="text-2xl" data-id="element-1527">📝</span>
                  </div>
                  <div data-id="element-1528">
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-1529">
                      Write Your Story
                    </h2>
                    <p className="text-gray-600" data-id="element-1530">
                      Type your memories and thoughts
                    </p>
                  </div>
                </div>

                <input type="text" placeholder="Story Title" className="w-full px-4 py-3 mb-4 text-xl font-semibold border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:outline-none transition-colors" data-id="element-1531" />

                <textarea placeholder="Start writing your story here..." className="w-full h-96 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:outline-none transition-colors resize-none" data-id="element-1532"></textarea>

                <div className="flex items-center justify-between mt-6" data-id="element-1533">
                  <button onClick={() => setShowEndModal(true)} className="px-6 py-3 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors font-medium" data-id="element-1534">
                    Cancel
                  </button>
                  <button onClick={handleEndConfirm} className="px-8 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all shadow-lg" data-id="element-1535">
                    Save Story
                  </button>
                </div>
              </div>
            </div>
          </div>}

        {mode === 'upload' && <div className="flex-1 flex items-center justify-center p-8" data-id="element-1536">
            <div className="max-w-2xl w-full" data-id="element-1537">
              <div className="bg-white rounded-3xl shadow-xl p-12 text-center" data-id="element-1538">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1539">
                  <span className="text-4xl" data-id="element-1540">📁</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1541">
                  Upload Media
                </h2>
                <p className="text-gray-600 mb-8 text-lg" data-id="element-1542">
                  Upload audio, video, or images to create your story
                </p>

                <div className="border-4 border-dashed border-gray-300 rounded-2xl p-12 mb-6 hover:border-purple-400 transition-colors cursor-pointer" data-id="element-1543">
                  <div className="text-gray-400 mb-4" data-id="element-1544">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1545">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" data-id="element-1546" />
                    </svg>
                  </div>
                  <p className="text-gray-900 font-semibold mb-2" data-id="element-1547">
                    Drop files here or click to browse
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-1548">
                    Supports MP4, MOV, MP3, WAV, JPG, PNG (Max 500MB)
                  </p>
                </div>

                <div className="flex gap-4" data-id="element-1549">
                  <button onClick={() => setShowEndModal(true)} className="flex-1 px-6 py-3 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors font-medium" data-id="element-1550">
                    Cancel
                  </button>
                  <button className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg" data-id="element-1551">
                    Upload Files
                  </button>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* End Recording Modal */}
      {showEndModal && <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-1552">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEndModal(false)} data-id="element-1553" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8" data-id="element-1554">
            <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1555">
              End Session?
            </h2>
            <p className="text-gray-600 mb-6" data-id="element-1556">
              {mode === 'record' ? 'Your recording will be stopped and saved.' : mode === 'journal' ? 'Your journal entry will be saved.' : 'Your upload session will end.'}
            </p>
            <div className="flex gap-3" data-id="element-1557">
              <button onClick={() => setShowEndModal(false)} className="flex-1 px-6 py-3 text-gray-700 font-semibold border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors" data-id="element-1558">
                Cancel
              </button>
              <button onClick={handleEndConfirm} className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg" data-id="element-1559">
                End Session
              </button>
            </div>
          </div>
        </div>}
    </div>;
}